/**
 * EntityBadge — single-entity UI primitive (no store, no fetch).
 *
 * Renders a CRM entity in one of four variants and three states:
 *   - `default`/`xs` — pill badges (rounded-full) backed by `Badge`
 *   - `inline` — flat chip (rounded, no ring, baseline-aligned) that
 *     matches the legacy Plate `EntityElement` so it can drop into
 *     flowing markdown / notes / proposal copy without visible drift.
 *   - `record` — 24px avatar + bold label, no pill. The odd one out, and
 *     deliberately so: the first three all mean "a record referenced from
 *     here", while `record` means "this IS the record". It exists for a
 *     primary-identifier column and should not be reached for elsewhere —
 *     see `EntityBadgeVariant` in `./types.ts` for the rule.
 *
 * The component is purely presentational: callers pass a fully-resolved
 * `EntityBadgeData` (or a loading/failed sentinel) and an optional
 * `onClick`. Wrappers in this directory add store hydration and
 * slideout-vs-page click translation on top.
 *
 * See `./types.ts` for the prop contract and issue #5032 for the
 * consolidation rationale.
 */

import { AlertCircle } from '@untitledui/icons';

import { Badge } from '@/lib/base/badges/badges';
import { useActivatable } from '@/lib/base/badges/useActivatable';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { cx } from '@/utils/cx';

import { EntityBadgeAvatar } from './EntityBadgeAvatar';
import {
  INLINE_CHIP_ADDED,
  INLINE_CHIP_BASE,
  INLINE_CHIP_DATA,
  INLINE_CHIP_FAILED,
} from './inline-chip-styles';
import {
  type EntityBadgeChangeMarker,
  type EntityBadgeData,
  type EntityBadgeState,
  type EntityBadgeVariant,
  WRAPPER_ALIGN_CLASSES,
} from './types';

const CHANGE_MARKER_LABELS: Record<EntityBadgeChangeMarker, string> = {
  added: 'Will be added',
  removed: 'Will be removed',
};

/**
 * Screen-reader announcement for a pending change.
 *
 * The visual treatment is tint (added) / strikethrough (removed), matching the
 * property-diff language used elsewhere in the same panel. Neither survives
 * assistive tech or forced-colors on its own (WCAG 1.4.1), so the state is
 * always also stated in text.
 *
 * Deliberately NOT a +/- glyph: a `+` inside a pill reads as an "add" button
 * rather than a state, and crowds the entity's own leading icon.
 */
function EntityBadgeChangeCue({ marker }: { marker: EntityBadgeChangeMarker }) {
  return <span className='sr-only'>{CHANGE_MARKER_LABELS[marker]}</span>;
}

export interface EntityBadgeProps {
  /** What to render. Discriminated union — see `EntityBadgeState`. */
  state: EntityBadgeState;
  /** Visual variant. Defaults to `default`. */
  variant?: EntityBadgeVariant;
  /**
   * When `true`, the leading element renders as a proper avatar
   * (image / initials / type-icon-in-container). When `false` (default),
   * a small leading icon is used instead.
   */
  avatar?: boolean;
  /**
   * When `false`, omit the leading avatar/icon element entirely — useful
   * when the surrounding context already conveys what the badge refers to
   * (e.g. an integration row that already shows a provider icon).
   * Defaults to `true`.
   */
  showLeading?: boolean;
  /**
   * When `true`, the badge renders as a non-interactive span and is
   * styled as visually de-emphasized (opacity reduction). Use for
   * "you can't do this right now" read-only contexts.
   */
  disabled?: boolean;
  /**
   * When `false`, the badge renders as a static span without the
   * cursor / hover / focus affordances even if an `onClick` is supplied.
   * Unlike `disabled`, this does NOT apply any visual de-emphasis —
   * the badge looks identical to its clickable counterpart, just
   * without the click state. Defaults to `true`.
   */
  interactive?: boolean;
  /**
   * Click handler. When omitted (or when `disabled`/`interactive=false`),
   * the badge renders as a static span. When supplied, it renders as a
   * button.
   */
  onClick?: () => void;
  /** Additional className applied to the outermost element. */
  className?: string;
  /**
   * Forwarded to the outermost rendered element. Wrappers (e.g. hover
   * preview) attach this so the `aria-describedby` relationship lives
   * on the focusable badge itself rather than on a non-focusable parent.
   */
  ariaDescribedBy?: string;
  /**
   * Forwarded to the interactive element. Wrappers (e.g. hover preview) set
   * this to `true` when their popover is open so screen readers announce the
   * expanded state on the focused button.
   */
  ariaExpanded?: boolean;
  /**
   * Forwarded to the interactive element. Wrappers (e.g. hover preview) set
   * this to describe what opens when the button is activated (e.g. 'dialog').
   */
  ariaHasPopup?: string;
  /**
   * When true AND `data.email` is set AND `data.email !== data.displayName`,
   * render the email in parentheses after the display name using a
   * de-emphasised text token. Defaults to false so non-opting callers are
   * unaffected.
   */
  showEmail?: boolean;
}

const FALLBACK_LABEL = 'Unavailable';

/**
 * Skeleton width per variant. Approximates the rendered badge width so
 * lists don't reflow when entities resolve.
 */
const SKELETON_WIDTH_BY_VARIANT: Record<EntityBadgeVariant, string> = {
  default: '7rem',
  inline: '5.5rem',
  xs: '5rem',
  // Widest of the four: it sits in a primary-name column (280px in every CRM
  // list config), so a narrow placeholder would jump on resolve.
  record: '9rem',
};

/**
 * Skeleton height per variant.
 */
const SKELETON_HEIGHT_BY_VARIANT: Record<EntityBadgeVariant, string> = {
  default: '1.5rem',
  inline: '1.25rem',
  xs: '1.125rem',
  // Matches the 24px avatar, which is the tallest thing in the row.
  record: '1.5rem',
};

/**
 * Skeleton border radius per variant — matches the rendered badge so
 * loading placeholders don't morph as state resolves.
 */
const SKELETON_RADIUS_BY_VARIANT: Record<EntityBadgeVariant, string> = {
  default: 'rounded-full',
  inline: 'rounded',
  xs: 'rounded-full',
  // Not a pill, so not pill-radiused — a rounded-full placeholder would
  // promise a chip that never arrives.
  record: 'rounded',
};

/**
 * Underlying base-Badge size for the pill variants. The `inline`
 * variant uses a custom container instead of `Badge`.
 */
const BADGE_SIZE_BY_PILL_VARIANT: Record<'default' | 'xs', 'sm' | 'md' | 'lg'> =
  {
    default: 'md',
    xs: 'sm',
  };

/**
 * Per-pill-variant tweaks to the badge's padding so the leading
 * avatar/icon sits flush. The asymmetric left padding (`pl-1`) makes
 * room for the leading element; when there is no leading element we
 * use symmetric padding instead (see `PILL_VARIANT_CLASSES_NO_LEADING`).
 *
 * `min-w-0` overrides the Badge primitive's default `min-width: auto`
 * (which would normally clamp at min-content of the longest unbreakable
 * word due to `whitespace-nowrap` on the badge). Without it, the inner
 * `truncate` text span can't shrink below its content's min-width and
 * `text-overflow: ellipsis` never engages — the text gets clipped at the
 * outer overflow boundary with no `…`. See CRM list cell rendering with
 * `wrap=false` (renderColumnCell.tsx).
 */
const PILL_VARIANT_CLASSES: Record<'default' | 'xs', string> = {
  default: 'gap-1 min-w-0 !py-0.5 !pl-1 !pr-2.5',
  xs: 'gap-0.5 min-w-0 !py-0 !pl-1 !pr-1.5 text-xs',
};

/**
 * Padding overrides for pill variants when `showLeading={false}`. Without
 * the leading element, the asymmetric `pl-1` from `PILL_VARIANT_CLASSES`
 * reads as a visual imbalance.
 */
const PILL_VARIANT_CLASSES_NO_LEADING: Record<'default' | 'xs', string> = {
  default: 'min-w-0 !py-0.5 !px-2.5',
  xs: 'min-w-0 !py-0 !px-1.5 text-xs',
};

/**
 * Which pill the non-pill `record` variant borrows for its unresolved-entity
 * error state. An error is a different kind of thing from the record it failed
 * to load — it has no avatar and no name — so it renders as the same error pill
 * every other variant uses rather than inventing a fourth error treatment.
 */
const ERROR_PILL_KEY_BY_VARIANT: Record<
  'default' | 'xs' | 'record',
  'default' | 'xs'
> = {
  default: 'default',
  xs: 'xs',
  record: 'default',
};

// Container classes for the `inline` chip variant live in
// ./inline-chip-styles.ts (shared with interactive chips that can't be an
// EntityBadge — see that module's docblock; imported at the top of this file).

/**
 * Wrapper class applied to the outermost span. Always applied
 * regardless of clickable vs static so the geometry stays identical.
 *
 * Alignment is *not* here — it comes from `WRAPPER_ALIGN_CLASSES` in `./types`,
 * because this component is not always the box that sits in the surrounding
 * line box. See that map for why.
 */
const WRAPPER_BASE_CLASSES = 'inline-flex my-px';

/**
 * The `record` variant's own container — it is not backed by `Badge`, so it
 * carries its layout here rather than through the pill maps.
 *
 * `gap-3` and the label's weight/colour are lifted verbatim from
 * `renderColumnCell`'s `renderObjectName`, the markup this variant is meant to
 * replace: the swap has to be pixel-identical or adopting it becomes a visual
 * change to every CRM list page rather than a refactor.
 *
 * `my-px` matches `WRAPPER_BASE_CLASSES`, which the loading and failed states
 * use: without it the cell would shift a pixel the moment the entity resolved.
 */
const RECORD_CONTAINER_CLASSES = cx(
  'inline-flex items-center gap-3 min-w-0 my-px',
  WRAPPER_ALIGN_CLASSES.record
);
const RECORD_LABEL_CLASSES = 'truncate font-medium text-primary';

export function EntityBadge({
  state,
  variant = 'default',
  avatar = false,
  showLeading = true,
  disabled = false,
  interactive = true,
  showEmail = false,
  onClick,
  className,
  ariaDescribedBy,
  ariaExpanded,
  ariaHasPopup,
}: EntityBadgeProps): React.ReactElement {
  let effectiveData: EntityBadgeData | undefined;
  if (state.kind === 'data') {
    effectiveData = state.data;
  } else if (state.kind === 'failed' && state.fallbackData) {
    effectiveData = state.fallbackData;
  }
  const isFallbackOnly = state.kind === 'failed' && Boolean(state.fallbackData);
  const isClickable =
    interactive && !disabled && !isFallbackOnly && Boolean(onClick);

  // Activation semantics (role/tabIndex/Enter+Space/stopPropagation) come from
  // the shared hook that lib/base Badge also uses, so the two cannot drift.
  // `onClick` is passed only when actually clickable, so a static badge gets no
  // role — matching the non-interactive branches below.
  const activatable = useActivatable({
    onClick: isClickable ? onClick : undefined,
  });

  // Loading state: skeleton sized + radius'd to approximate the resolved badge.
  // Render as `<span>` so the badge can sit inside flow content (markdown
  // <p>, notes, proposal copy) without producing invalid `<div>`-in-`<p>`
  // HTML — that mismatch breaks React hydration in the surrounding subtree.
  if (state.kind === 'loading') {
    return (
      <span
        className={cx(
          WRAPPER_BASE_CLASSES,
          WRAPPER_ALIGN_CLASSES[variant],
          className
        )}
        data-testid='entity-badge-loading'
        data-component='entity-badge'
        data-state='loading'
        aria-describedby={ariaDescribedBy}
      >
        <Skeleton
          as='span'
          variant='rectangular'
          width={SKELETON_WIDTH_BY_VARIANT[variant]}
          height={SKELETON_HEIGHT_BY_VARIANT[variant]}
          className={SKELETON_RADIUS_BY_VARIANT[variant]}
        />
      </span>
    );
  }

  // Failed state without fallback data — distinct render per variant so the chip stays a chip.
  // When fallbackData is present we fall through to the data-rendering path below.
  if (state.kind === 'failed' && !state.fallbackData) {
    const label = state.fallbackLabel ?? FALLBACK_LABEL;
    if (variant === 'inline') {
      return (
        <span
          className={cx(
            INLINE_CHIP_BASE,
            INLINE_CHIP_FAILED,
            'text-[var(--color-text-error-primary)]',
            className
          )}
          data-testid='entity-badge-failed'
          data-component='entity-badge'
          data-state='failed'
          title={label}
          aria-describedby={ariaDescribedBy}
        >
          <AlertCircle className='size-3.5 shrink-0' aria-hidden />
          <span className='truncate min-w-0'>{label}</span>
        </span>
      );
    }
    return (
      <span
        className={cx(
          WRAPPER_BASE_CLASSES,
          WRAPPER_ALIGN_CLASSES[variant],
          className
        )}
        data-testid='entity-badge-failed'
        data-component='entity-badge'
        data-state='failed'
        title={label}
        aria-describedby={ariaDescribedBy}
      >
        <Badge
          type='pill-color'
          color='error'
          size={BADGE_SIZE_BY_PILL_VARIANT[ERROR_PILL_KEY_BY_VARIANT[variant]]}
          className={cx(
            PILL_VARIANT_CLASSES[ERROR_PILL_KEY_BY_VARIANT[variant]]
          )}
        >
          <AlertCircle
            className={cx(variant === 'xs' ? 'size-2.5' : 'size-3', 'shrink-0')}
            aria-hidden
          />
          <span className='truncate min-w-0'>{label}</span>
        </Badge>
      </span>
    );
  }

  // Data state (or failed state with fallbackData — rendered as a non-interactive gray pill).
  if (!effectiveData) {
    return <span />;
  }
  const data = effectiveData;
  const shouldShowEmail = Boolean(
    showEmail && data.email && data.email !== data.displayName
  );
  const ariaLabel = isClickable ? `View ${data.displayName}` : data.displayName;

  // Inline variant — flat chip (matches Plate `EntityElement`).
  if (variant === 'inline') {
    const chipContent = (
      <>
        {data.changeMarker && (
          <EntityBadgeChangeCue marker={data.changeMarker} />
        )}
        {showLeading && (
          <EntityBadgeAvatar
            entityType={data.entityType}
            displayName={data.displayName}
            avatarUrl={data.avatarUrl}
            avatar={avatar}
            variant='inline'
            status={data.status}
          />
        )}
        <span
          className={cx(
            'truncate min-w-0',
            isClickable && 'group-hover/entity-badge:underline',
            data.changeMarker === 'removed' && 'line-through text-fg-quaternary'
          )}
        >
          {data.displayName}
        </span>
        {shouldShowEmail && (
          <span
            className='truncate min-w-0 font-normal text-fg-tertiary'
            data-testid='entity-badge-email'
          >
            ({data.email})
          </span>
        )}
      </>
    );

    if (!isClickable) {
      return (
        <span
          className={cx(
            INLINE_CHIP_BASE,
            data.changeMarker === 'added'
              ? INLINE_CHIP_ADDED
              : INLINE_CHIP_DATA,
            data.changeMarker === 'removed' && 'opacity-70',
            disabled && 'opacity-60',
            className
          )}
          data-component='entity-badge'
          data-state='loaded'
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          title={data.displayName}
          data-entity-id={data.id}
        >
          {chipContent}
        </span>
      );
    }

    return (
      <span
        {...activatable}
        className={cx(
          INLINE_CHIP_BASE,
          data.changeMarker === 'added' ? INLINE_CHIP_ADDED : INLINE_CHIP_DATA,
          data.changeMarker === 'removed' && 'opacity-70',
          'group/entity-badge cursor-pointer',
          'focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
          className
        )}
        data-component='entity-badge'
        data-state='loaded'
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup as React.AriaAttributes['aria-haspopup']}
        title={data.displayName}
        data-entity-id={data.id}
      >
        {chipContent}
      </span>
    );
  }

  // Record variant — the row's own record: avatar + bold label, no pill.
  //
  // Returns before the pill path below, which is also what narrows `variant`
  // to the two pill keys for the maps that follow.
  if (variant === 'record') {
    const recordContent = (
      <>
        {data.changeMarker && (
          <EntityBadgeChangeCue marker={data.changeMarker} />
        )}
        {showLeading && (
          <EntityBadgeAvatar
            entityType={data.entityType}
            displayName={data.displayName}
            avatarUrl={data.avatarUrl}
            avatar={avatar}
            variant='record'
            status={data.status}
          />
        )}
        <span
          className={cx(
            RECORD_LABEL_CLASSES,
            isClickable && 'group-hover/entity-badge:underline',
            // Success tint carries "this is being added", matching the green the
            // pill/inline variants use one row above. Removals strike the label
            // instead — same language, opposite sign.
            data.changeMarker === 'added' && 'text-utility-success-700',
            data.changeMarker === 'removed' && 'line-through text-fg-quaternary'
          )}
        >
          {data.displayName}
        </span>
        {shouldShowEmail && (
          <span
            className='truncate min-w-0 font-normal text-fg-tertiary'
            data-testid='entity-badge-email'
          >
            ({data.email})
          </span>
        )}
      </>
    );

    if (!isClickable) {
      return (
        <span
          className={cx(
            RECORD_CONTAINER_CLASSES,
            'max-w-full',
            data.changeMarker === 'removed' && 'opacity-70',
            disabled && 'opacity-60',
            className
          )}
          data-component='entity-badge'
          data-state='loaded'
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          title={data.displayName}
          data-entity-id={data.id}
        >
          {recordContent}
        </span>
      );
    }

    return (
      <span
        {...activatable}
        className={cx(
          RECORD_CONTAINER_CLASSES,
          'max-w-full',
          data.changeMarker === 'removed' && 'opacity-70',
          'group/entity-badge cursor-pointer',
          // `rounded` rather than the pill variants' `rounded-full`: the focus
          // ring traces the actual shape, and this one is not a pill.
          'rounded focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
          className
        )}
        data-component='entity-badge'
        data-state='loaded'
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup as React.AriaAttributes['aria-haspopup']}
        title={data.displayName}
        data-entity-id={data.id}
      >
        {recordContent}
      </span>
    );
  }

  // Default / xs — pill badge backed by `Badge`.
  const pillContent = (
    <Badge
      type='pill-color'
      // Tint carries "this is being added", matching the green the property
      // diff uses for an updated value one row above. Removals keep the gray
      // pill and strike the label instead — same language, opposite sign.
      color={data.changeMarker === 'added' ? 'success' : 'gray'}
      size={BADGE_SIZE_BY_PILL_VARIANT[variant]}
      className={cx(
        showLeading
          ? PILL_VARIANT_CLASSES[variant]
          : PILL_VARIANT_CLASSES_NO_LEADING[variant],
        data.changeMarker === 'removed' && 'opacity-70'
      )}
    >
      {data.changeMarker && <EntityBadgeChangeCue marker={data.changeMarker} />}
      {showLeading && (
        <EntityBadgeAvatar
          entityType={data.entityType}
          displayName={data.displayName}
          avatarUrl={data.avatarUrl}
          avatar={avatar}
          variant={variant}
          status={data.status}
        />
      )}
      <span
        className={cx(
          'truncate',
          isClickable && 'group-hover/entity-badge:underline',
          data.changeMarker === 'removed' && 'line-through text-fg-quaternary'
        )}
      >
        {data.displayName}
      </span>
      {shouldShowEmail && (
        <span
          className='truncate min-w-0 font-normal text-fg-tertiary'
          data-testid='entity-badge-email'
        >
          ({data.email})
        </span>
      )}
    </Badge>
  );

  if (!isClickable) {
    return (
      <span
        className={cx(
          WRAPPER_BASE_CLASSES,
          WRAPPER_ALIGN_CLASSES[variant],
          'min-w-0 max-w-full',
          'rounded-full',
          disabled && 'opacity-60',
          className
        )}
        data-component='entity-badge'
        data-state='loaded'
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        title={data.displayName}
        data-entity-id={data.id}
      >
        {pillContent}
      </span>
    );
  }

  return (
    <span
      {...activatable}
      className={cx(
        WRAPPER_BASE_CLASSES,
        WRAPPER_ALIGN_CLASSES[variant],
        'group/entity-badge cursor-pointer min-w-0 max-w-full',
        'rounded-full',
        'focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
        className
      )}
      data-component='entity-badge'
      data-state='loaded'
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup as React.AriaAttributes['aria-haspopup']}
      title={data.displayName}
      data-entity-id={data.id}
    >
      {pillContent}
    </span>
  );
}
